import React, { useState, useEffect } from 'react';
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  MenuItem,
  Divider,
  Box
} from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  KeyboardTimePicker
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

const SelectTime = ({
  mode = 3,
  value,
  onChange,
  disabled = false,
  variant = 'outlined',
  helperText = '',
  error = false,
  size = 'small'
}) => {
  const normalizeDate = val => {
    if (!val) return null;
    if (val instanceof Date && !isNaN(val)) return val;
    const d = new Date(val);
    return isNaN(d) ? null : d;
  };

  const [localTime, setLocalTime] = useState(
    normalizeDate(value) || new Date()
  );
  const [manualText, setManualText] = useState(
    normalizeDate(value)
      ? normalizeDate(value).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })
      : ''
  );

  const [open, setOpen] = useState(false);

  useEffect(() => {
    const d = normalizeDate(value);
    if (d) {
      setLocalTime(d);
      setManualText(
        d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    }
  }, [value]);

  // Whenever localTime changes, update manualText
  useEffect(() => {
    setManualText(
      localTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  }, [localTime]);

  const emitChange = date => {
    if (!date) return;
    setLocalTime(date);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Dropdown helper
  const setPart = (h, m, a) => {
    let finalHour = h;
    if (a === 'PM' && h !== 12) finalHour += 12;
    if (a === 'AM' && h === 12) finalHour = 0;
    const newDate = new Date(localTime);
    newDate.setHours(finalHour, m);
    emitChange(newDate);
  };

  // Recompute hour/minute/AMPM dynamically
  const hour = localTime.getHours();
  const minute = localTime.getMinutes();
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;

  return (
    <>
      <TextField
        fullWidth
        value={manualText}
        onClick={handleOpen}
        placeholder="Select time"
        variant={variant}
        size={size}
        disabled={disabled}
        error={error}
        helperText={helperText}
        InputProps={{
          readOnly: true, // prevents keyboard typing
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={handleOpen} edge="end">
                <AccessTimeIcon color="primary" />
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Select Time</DialogTitle>
        <Box mb={2}>
          <Divider />
        </Box>
        <DialogContent>
          {mode === 0 && (
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardTimePicker
                value={localTime}
                onChange={emitChange}
                fullWidth
                disabled={disabled}
                KeyboardButtonProps={{ 'aria-label': 'change time' }}
              />
            </MuiPickersUtilsProvider>
          )}

          {mode === 1 && (
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <TextField
                  select
                  label="Hour"
                  value={displayHour}
                  fullWidth
                  disabled={disabled}
                  onChange={e => setPart(+e.target.value, minute, ampm)}
                  variant={variant}
                  size={size}
                >
                  {[...Array(12)].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  select
                  label="Minute"
                  value={minute}
                  fullWidth
                  disabled={disabled}
                  onChange={e => setPart(displayHour, +e.target.value, ampm)}
                  variant={variant}
                  size={size}
                >
                  {[...Array(61)].map((_, i) => (
                    <MenuItem key={i} value={i}>
                      {String(i).padStart(2, '0')}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  select
                  label="AM/PM"
                  value={ampm}
                  fullWidth
                  disabled={disabled}
                  onChange={e => setPart(displayHour, minute, e.target.value)}
                  variant={variant}
                  size={size}
                >
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}

          {mode === 2 && (
            <TextField
              fullWidth
              placeholder="hh:mm AM"
              value={manualText}
              disabled={disabled}
              onChange={e => {
                const val = e.target.value.toUpperCase();
                setManualText(val);
                const d = new Date(`1970-01-01 ${val}`);
                if (!isNaN(d)) emitChange(d);
              }}
              size={size}
              variant={variant}
              error={error}
              helperText={helperText}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={() => {
              onChange && onChange(localTime);
              handleClose();
            }}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectTime;
